/**
 * B1, lado de cliente (13/09/2026). Hasta hoy una excepción del navegador no dejaba
 * rastro en ninguna parte: `console.error` se anula dos veces antes de que exista la
 * aplicación (main.ts y el APP_INITIALIZER de app.module.ts) y el `ErrorHandler` propio
 * termina llamando a ese `console.error` que ya no existe. El incidente del 12/09 se vio
 * por los 404 del registro de nginx, no por el front.
 *
 * Esto manda un resumen —mensaje, tres líneas de pila, URL, build y user-agent— a
 * `POST /api/cliente-error` con `navigator.sendBeacon`, que sigue vivo aunque la pestaña
 * se esté cerrando y no necesita leer la respuesta (el servidor contesta 204).
 *
 * Cuatro decisiones que conviene no deshacer sin pensarlas:
 *
 *   - **Se manda una cadena, no un Blob.** `sendBeacon(url, cadena)` viaja como
 *     `text/plain;charset=UTF-8`, que es una petición simple: sin preflight. Con un Blob
 *     de `application/json` el navegador pediría OPTIONS antes, y app.michollo.com es
 *     otro origen. El servidor lee el cuerpo como texto y lo parsea él, así que acepta
 *     los dos; se elige el que no gasta un viaje de más.
 *   - **Muestreo por SESIÓN, no por error.** El sorteo se hace una vez y se guarda: o se
 *     ve una sesión entera o no se ve ninguna. Diez informes sueltos de diez sesiones
 *     distintas no cuentan la historia que cuentan los cinco de una sola.
 *   - **Nada que identifique a nadie.** Ni contenido de formularios, ni cabeceras, ni
 *     cuerpos de petición, ni la URL con sus parámetros. Del error se cogen dos campos,
 *     `message` y `stack`, y se pasan por `limpiarTexto`; de un AxiosError eso deja fuera
 *     `config` (que lleva la cabecera Authorization y el cuerpo enviado), `request` y
 *     `response.data`. El mensaje en sí también se tacha, porque el de un
 *     `HttpErrorResponse` de Angular incluye la URL entera —parámetros incluidos—.
 *   - **No puede hacer ruido.** Todo va dentro de try/catch: un fallo del vigilante de
 *     errores que provoque otro error sería el peor de los mundos.
 */

/** Destino en producción. El navegador habla siempre con app.michollo.com (ver rest.service). */
export const DESTINO = 'https://app.michollo.com/api/cliente-error';

/**
 * Cinco informes por sesión y una décima parte del tráfico. Las dos cifras salen de las
 * ~700 visitas/hora reales:
 *
 *   - **Tope de 5.** Un bucle roto en un `ngFor` dispara cientos de excepciones iguales en
 *     segundos; a partir del segundo o tercer informe no se aprende nada nuevo. Además el
 *     endpoint corta a 120/hora por IP: con 5 por sesión harían falta 24 sesiones de la
 *     misma IP fallando dentro de la misma hora para ver un 429, y aquí no se reintenta
 *     nada, así que un 429 no se convertiría en una tormenta.
 *   - **Fracción 0,1.** A 700 visitas/hora son 16.800 sesiones al día. En el peor caso
 *     imaginable —un despliegue que rompa TODAS las páginas— serían 16.800 × 5 = 84.000
 *     líneas de ~400 B, 34 MB/día, 1 GB en los 30 días que guarda logrotate: eso llena el
 *     disco. Con una décima parte son 3,4 MB/día y ~100 MB de pico, que el disco aguanta.
 *     Y por el otro lado sigue viéndose lo pequeño: un fallo que afecte al 1 % de las
 *     sesiones deja unos 17 informes al día, de sobra para que salte a la vista en un grep.
 *
 * Con `?debug=1` no hay sorteo (el tope sí sigue), que es justo lo que hace falta para
 * comprobar algo en producción sin desplegar.
 */
export const TOPE_POR_SESION = 5;
export const FRACCION_DE_SESIONES = 0.1;

/** Topes del contrato (docs/openapi.yaml, ErrorDeCliente). Recortar aquí evita un 400. */
export const LIMITES = { mensaje: 300, pila: 3, linea: 300, url: 500, build: 64, agente: 256 };

export const CLAVE_DEPURACION = 'mc-depurar';
export const CLAVE_SORTEO = 'mc-informes-sorteo';
export const CLAVE_CUENTA = 'mc-informes-enviados';

/**
 * La consola de verdad, capturada al evaluarse el módulo. main.ts la anula en su primera
 * instrucción, pero los `import` se evalúan antes que el cuerpo del fichero que los pide,
 * así que esta referencia es anterior al silenciado pase lo que pase.
 */
export const consolaOriginal: (...datos: unknown[]) => void =
  typeof console !== 'undefined' && console.error ? console.error.bind(console) : () => {};

// ---------------------------------------------------------------------------
// Parte pura: recorte, tachado y muestreo. Todo lo de aquí se prueba en el spec.
// ---------------------------------------------------------------------------

// Los cinco tokens que los motores de JavaScript meten en las pilas. El servidor los deja
// pasar y son los únicos `<>` que sobreviven al tachado; el resto se va, porque distinguir
// «etiqueta de verdad» de «signo suelto» se equivoca antes o después.
// Se buscan los tokens y los ángulos sueltos en UNA pasada, y la función decide: lo que
// mide más de un carácter es un token de pila y se queda tal cual; lo que mide uno es un
// `<` o un `>` suelto y se va. Con dos pasadas y un hueco intermedio, un mensaje que ya
// trajera la palabra del hueco saldría convertido en un token que nadie escribió.
const ANGULOS = /<(?:anonymous|computed|eval|native|unknown)>|[<>]/g;

// Caracteres de control, más U+2028/U+2029: romperían el «un objeto por línea» del NDJSON.
// (no-control-regex está apagada para este fichero en eslint.config.js: los caracteres de
// control son justo lo que se busca. Un `eslint-disable` en línea aquí no sobrevive: lo
// quita cualquier `eslint --fix` con una configuración que no tenga esa regla activa.)
const CONTROL = /[\u0000-\u001f\u007f\u2028\u2029]+/g;

/**
 * Lo que se tacha, por orden. El mensaje de un `HttpErrorResponse` es
 * «Http failure response for https://app.michollo.com/api/auth/login?token=…: 401», o sea
 * que la URL con sus parámetros viaja dentro del texto: hay que mirar el texto, no sólo
 * `location`.
 */
const TACHONES: Array<[RegExp, string]> = [
  // Parámetros y fragmento de cualquier URL que aparezca en el texto.
  [/(https?:\/\/[^\s"'?#]*)[?#][^\s"']*/gi, '$1?…'],
  // Cabecera Authorization escrita a mano dentro de un mensaje.
  [/\b(bearer|basic)\s+[\w.\-~+/=]+/gi, '$1 …'],
  // Un JWT suelto (los tres trozos separados por puntos).
  [/\beyJ[\w-]{6,}\.[\w-]{6,}\.[\w-]+/g, '…'],
  // Correos.
  [/\b[\w.%+-]+@[\w.-]+\.[A-Za-z]{2,}\b/g, '…'],
  // Tiradas largas de letras y dígitos SIN separadores: claves de API, identificadores de
  // sesión, hashes. El guion queda fuera del patrón a propósito: los slugs de michollo son
  // largos y van con guiones («auriculares-sony-wh-1000xm5»), y tacharlos dejaría los
  // informes sin lo único que dice DÓNDE se rompió. Una clave sin un solo guion en 32
  // caracteres es lo normal; un slug sin un solo guion en 32, no.
  [/[A-Za-z0-9]{32,}/g, '…'],
];

/**
 * Deja un texto listo para el endpoint: tachado, sin controles, sin `<>` (salvo los tokens
 * de pila) y recortado. Devuelve cadena vacía si no queda nada.
 */
export function limpiarTexto(valor: unknown, max: number): string {
  let texto = typeof valor === 'string' ? valor : String(valor ?? '');

  for (const [patron, reemplazo] of TACHONES) texto = texto.replace(patron, reemplazo);

  texto = texto.replace(CONTROL, ' ');
  texto = texto.replace(ANGULOS, (casa) => (casa.length > 1 ? casa : ''));
  texto = texto.replace(/\s+/g, ' ').trim();

  return texto.length > max ? `${texto.slice(0, max - 1).trim()}…` : texto;
}

/** El mensaje de un error venga como venga: Error, cadena, rechazo de promesa envuelto… */
export function mensajeDeError(motivo: unknown): string {
  const error = motivo as { message?: unknown; name?: unknown; rejection?: unknown } | null;

  // Angular envuelve el rechazo de una promesa en un error propio con `.rejection` dentro.
  if (error && typeof error === 'object' && error.rejection !== undefined && error.rejection !== motivo) {
    const dentro = mensajeDeError(error.rejection);
    if (dentro && dentro !== 'error sin mensaje') return dentro;
  }

  const nombre = typeof error?.name === 'string' ? error.name : '';
  const mensaje = typeof error?.message === 'string' ? error.message : '';
  const crudo = mensaje
    ? (nombre && !mensaje.startsWith(nombre) ? `${nombre}: ${mensaje}` : mensaje)
    : String(motivo ?? '');

  const limpio = limpiarTexto(crudo, LIMITES.mensaje);
  // El campo es obligatorio y el servidor rechaza el vacío; un error sin mensaje sigue
  // siendo una noticia (dice que algo pasó, y la pila lo sitúa).
  return limpio || 'error sin mensaje';
}

/**
 * Las tres primeras líneas útiles de la pila. Se prefieren las que parecen un marco
 * (`at algo (fichero)` en Chrome, `algo@fichero` en Firefox) para no gastar una de las tres
 * en repetir el mensaje, que ya va en su propio campo.
 */
export function lineasDePila(pila: unknown, max = LIMITES.pila): string[] {
  if (typeof pila !== 'string' || !pila) return [];

  const lineas = pila.split('\n').map((l) => l.trim()).filter(Boolean);
  // Un marco de Chrome empieza por «at»; uno de Firefox es `función@fichero:línea:columna`.
  // Lo de Firefox se pide con el `:línea:columna` al final y no con un `@` a secas: si no,
  // un mensaje que mencione un correo cuela la línea del mensaje como si fuera un marco y
  // gasta una de las tres (visto en la prueba de extremo a extremo con un 401 de Angular).
  const marcos = lineas.filter((l) => /^at\s/.test(l) || /@\S*:\d+:\d+\)?$/.test(l));
  const elegidas = (marcos.length ? marcos : lineas).slice(0, max);

  return elegidas.map((l) => limpiarTexto(l, LIMITES.linea)).filter(Boolean);
}

/**
 * La URL sin nada detrás: origen y camino. Los parámetros se van enteros —ahí es donde
 * viajan los tokens de recuperación de contraseña y los `?email=`— y el fragmento también.
 */
export function urlSegura(href: unknown): string | undefined {
  if (typeof href !== 'string' || !/^https?:\/\//i.test(href)) return undefined;

  const cortada = href.split('?')[0].split('#')[0];
  const limpia = limpiarTexto(cortada, LIMITES.url);
  return /^https?:\/\/.+/i.test(limpia) ? limpia : undefined;
}

/**
 * El hash del build, sacado del nombre del fichero que sirve la aplicación
 * (`main.4f3a91c.js`). Así no hace falta tocar la compilación para tener una versión con la
 * que comparar dos informes.
 */
export function buildDeLosScripts(fuentes: Iterable<string>): string | undefined {
  for (const src of fuentes) {
    // Los bundles de esta aplicación llevan GUION, no punto: `main-6S4UTF7E.js`. Con el
    // punto el campo `build` habría llegado siempre vacío y no se podría saber de qué
    // despliegue viene un error, que es justo para lo que sirve. Lo cazó el verificador.
    const casa = /\bmain[.-]([A-Za-z0-9]{6,32})\.js\b/.exec(src || '');
    if (casa) return casa[1].slice(0, LIMITES.build);
  }
  return undefined;
}

export interface Informe {
  mensaje: string;
  pila?: string[];
  url?: string;
  build?: string;
  agente?: string;
}

/** Arma el cuerpo del informe. Puro: se le dan los datos, no los busca. */
export function componerInforme(
  motivo: unknown,
  contexto: { url?: string; build?: string; agente?: string } = {},
): Informe {
  const error = motivo as { stack?: unknown } | null;
  const informe: Informe = { mensaje: mensajeDeError(motivo) };

  const pila = lineasDePila(error?.stack);
  if (pila.length) informe.pila = pila;

  const url = urlSegura(contexto.url);
  if (url) informe.url = url;

  // El contrato sólo admite letras, dígitos y . _ : - en `build`.
  const build = typeof contexto.build === 'string'
    ? contexto.build.replace(/[^A-Za-z0-9._:-]/g, '').slice(0, LIMITES.build)
    : '';
  if (build) informe.build = build;

  const agente = limpiarTexto(contexto.agente, LIMITES.agente);
  if (agente) informe.agente = agente;

  return informe;
}

/** Huella para no repetir el mismo error cinco veces: mensaje más el primer marco. */
export function huella(informe: Informe): string {
  return `${informe.mensaje}|${informe.pila?.[0] ?? ''}`;
}

/** El sorteo del muestreo, con el azar inyectable para poder probarlo. */
export function sortear(fraccion = FRACCION_DE_SESIONES, azar: () => number = Math.random): boolean {
  return azar() < fraccion;
}

/** ¿Cabe un informe más? Puro: decide con el estado que se le pasa, no con el del navegador. */
export function cabeOtroInforme(
  estado: { enviados: number; sorteada: boolean; depurando?: boolean },
  tope = TOPE_POR_SESION,
): boolean {
  if (estado.enviados >= tope) return false;
  return estado.sorteada || !!estado.depurando;
}

// ---------------------------------------------------------------------------
// Parte con estado: almacenamiento, interruptor de depuración y el envío.
// ---------------------------------------------------------------------------

type Almacen = Pick<Storage, 'getItem' | 'setItem'>;

function almacenDe(nombre: 'sessionStorage' | 'localStorage'): Almacen | null {
  try {
    const almacen = (globalThis as unknown as Record<string, Almacen | undefined>)[nombre];
    return almacen ?? null;
  } catch { return null; }
}

function leer(almacen: Almacen | null, clave: string): string | null {
  try { return almacen?.getItem(clave) ?? null; } catch { return null; }
}

function escribir(almacen: Almacen | null, clave: string, valor: string): void {
  try { almacen?.setItem(clave, valor); } catch { /* modo privado estricto: se sigue en memoria */ }
}

// Respaldo en memoria para cuando no hay sessionStorage (modo privado estricto): sin esto
// el tope no existiría justo en el navegador donde más barato sería mandar de más.
let enviadosEnMemoria = 0;
let sorteoEnMemoria: boolean | null = null;
const vistos = new Set<string>();

/**
 * El interruptor para depurar en producción sin desplegar: `?debug=1` en la URL, o la clave
 * `mc-depurar` en localStorage. El parámetro se guarda al verlo, para que siga valiendo
 * después de navegar dentro de la aplicación; `?debug=0` lo apaga.
 */
export function depuracionActiva(
  busqueda: string = typeof location === 'undefined' ? '' : location.search,
  almacen: Almacen | null = almacenDe('localStorage'),
): boolean {
  const pedido = /[?&]debug=([01])/.exec(busqueda || '');

  if (pedido) {
    escribir(almacen, CLAVE_DEPURACION, pedido[1]);
    return pedido[1] === '1';
  }

  return leer(almacen, CLAVE_DEPURACION) === '1';
}

/** El sorteo de la sesión, hecho una vez y recordado. */
function sesionSorteada(): boolean {
  const almacen = almacenDe('sessionStorage');
  const guardado = leer(almacen, CLAVE_SORTEO);
  if (guardado === '1' || guardado === '0') return guardado === '1';

  if (sorteoEnMemoria === null) sorteoEnMemoria = sortear();
  escribir(almacen, CLAVE_SORTEO, sorteoEnMemoria ? '1' : '0');
  return sorteoEnMemoria;
}

function enviados(): number {
  const guardado = Number(leer(almacenDe('sessionStorage'), CLAVE_CUENTA));
  return Number.isFinite(guardado) && guardado > enviadosEnMemoria ? guardado : enviadosEnMemoria;
}

function apuntarEnvio(): void {
  enviadosEnMemoria = enviados() + 1;
  escribir(almacenDe('sessionStorage'), CLAVE_CUENTA, String(enviadosEnMemoria));
}

/**
 * Dónde se manda. En producción, siempre app.michollo.com. En localhost —y sólo ahí— se
 * admite un destino puesto por la página, que es lo que usa la prueba con Chromium contra
 * un servidor local; atarlo a localhost impide que un XSS en producción se lleve los
 * informes a otro sitio.
 */
export function destinoDeInformes(
  ventana: unknown = typeof window === 'undefined' ? undefined : window,
): string {
  try {
    const v = ventana as { location?: { hostname?: string }; __mcDestinoErrores?: unknown } | undefined;
    const host = v?.location?.hostname ?? '';
    const local = host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
    if (local && typeof v?.__mcDestinoErrores === 'string' && v.__mcDestinoErrores) return v.__mcDestinoErrores;
  } catch { /* da igual: se usa el destino de siempre */ }
  return DESTINO;
}

/**
 * Manda el resumen de un error, si toca. Devuelve true sólo si el informe salió de verdad,
 * que es lo que comprueba la prueba de extremo a extremo.
 *
 * Nunca lanza: lo llama un `ErrorHandler`, y un fallo aquí dentro sería un error dentro del
 * manejador de errores.
 */
export function mandarInformeDeError(motivo: unknown): boolean {
  try {
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return false;

    const depurando = depuracionActiva();
    if (depurando) consolaOriginal(motivo);

    if (!cabeOtroInforme({ enviados: enviados(), sorteada: sesionSorteada(), depurando })) return false;

    const informe = componerInforme(motivo, {
      url: typeof location === 'undefined' ? undefined : location.href,
      build: typeof document === 'undefined'
        ? undefined
        : buildDeLosScripts(Array.from(document.getElementsByTagName('script')).map((s) => s.src)),
      agente: navigator.userAgent,
    });

    // El mismo error repetido no cuenta contra el tope ni ensucia el fichero: un bucle roto
    // manda UNA línea, no cinco iguales.
    const marca = huella(informe);
    if (vistos.has(marca)) return false;

    // Cadena, no Blob: text/plain viaja sin preflight (ver cabecera del fichero).
    const salio = navigator.sendBeacon(destinoDeInformes(), JSON.stringify(informe));

    // Se apunta DESPUÉS y sólo si salió: `sendBeacon` devuelve false cuando la cola del
    // navegador está llena, y ese informe no se ha contado a nadie —darlo por visto sería
    // perderlo para siempre—.
    if (salio) { vistos.add(marca); apuntarEnvio(); }
    return salio;
  } catch {
    return false;
  }
}
